import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Edit, Trash2, GripVertical, Star } from 'lucide-react';
import { usePipelines } from '@/composables/crm/usePipeline';
import { pipelineSchema, stageSchema, type PipelineFormData, type StageFormData } from '@/lib/validators';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/FormDialog';
import { FormField } from '@/components/forms/FormField';
import { FormTextarea } from '@/components/forms/FormTextarea';
import type { Pipeline, DealStage } from '@/types/table';

export function PipelineSettingsPage() {
  const { pipelines, isLoading, createPipeline, updatePipeline, deletePipeline, addStage, updateStage, deleteStage } = usePipelines();

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteStageInfo, setDeleteStageInfo] = useState<{ pipelineId: string; stageId: string } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editPipeline, setEditPipeline] = useState<Pipeline | null>(null);
  const [addStageTo, setAddStageTo] = useState<string | null>(null);
  const [editStageInfo, setEditStageInfo] = useState<{ pipelineId: string; stage: DealStage } | null>(null);

  const pipelineForm = useForm<PipelineFormData>({
    resolver: zodResolver(pipelineSchema),
    defaultValues: { name: '', description: '', isDefault: false },
  });

  const stageForm = useForm<StageFormData>({
    resolver: zodResolver(stageSchema),
    defaultValues: { name: '', probability: 0, color: '' },
  });

  const handleCreate = (data: PipelineFormData) => {
    createPipeline.mutate(data, { onSuccess: () => { setShowCreate(false); pipelineForm.reset(); } });
  };

  const handleEditPipeline = (pipeline: Pipeline) => {
    setEditPipeline(pipeline);
    pipelineForm.reset({ name: pipeline.name, description: pipeline.description || '', isDefault: pipeline.isDefault });
  };

  const handleUpdatePipeline = (data: PipelineFormData) => {
    if (!editPipeline) return;
    updatePipeline.mutate({ id: editPipeline.id, data }, { onSuccess: () => { setEditPipeline(null); pipelineForm.reset(); } });
  };

  const handleOpenAddStage = (pipelineId: string) => {
    setAddStageTo(pipelineId);
    stageForm.reset({ name: '', probability: 0, color: '' });
  };

  const handleCreateStage = (data: StageFormData) => {
    if (!addStageTo) return;
    const pipeline = pipelines.find(p => p.id === addStageTo);
    const position = pipeline?.stages?.length ?? 0;
    addStage.mutate({ pipelineId: addStageTo, data: { ...data, position } }, { onSuccess: () => { setAddStageTo(null); stageForm.reset(); } });
  };

  const handleEditStage = (pipelineId: string, stage: DealStage) => {
    setEditStageInfo({ pipelineId, stage });
    stageForm.reset({ name: stage.name, probability: stage.probability, color: stage.color || '' });
  };

  const handleUpdateStage = (data: StageFormData) => {
    if (!editStageInfo) return;
    updateStage.mutate({ pipelineId: editStageInfo.pipelineId, stageId: editStageInfo.stage.id, data }, { onSuccess: () => { setEditStageInfo(null); stageForm.reset(); } });
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <PageContainer title="Pipeline Settings" description="Configure sales pipelines and stages. The % on each stage is the win probability used for revenue forecasting." actions={<Button onClick={() => { setShowCreate(true); pipelineForm.reset(); }}><Plus className="mr-2 h-4 w-4" />New Pipeline</Button>}>
      {pipelines.length === 0 ? <EmptyState title="No pipelines" description="Create your first sales pipeline" actionLabel="Create Pipeline" onAction={() => setShowCreate(true)} /> : (
        <div className="space-y-6">
          {pipelines.map((pipeline) => (
            <Card key={pipeline.id}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">{pipeline.name}</CardTitle>
                  {pipeline.isDefault && <Badge variant="secondary">Default</Badge>}
                  {!pipeline.isActive && <Badge variant="destructive">Inactive</Badge>}
                </div>
                <div className="flex gap-1">
                  {!pipeline.isDefault && (
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => updatePipeline.mutate({ id: pipeline.id, data: { isDefault: true } })}>
                      <Star className="mr-1 h-3.5 w-3.5" />Set Default
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditPipeline(pipeline)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteId(pipeline.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </CardHeader>
              <CardContent>
                {pipeline.description && <p className="text-sm text-muted-foreground mb-4">{pipeline.description}</p>}
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium">Stages</h4>
                    <Button variant="outline" size="sm" onClick={() => handleOpenAddStage(pipeline.id)}><Plus className="mr-1 h-3 w-3" />Add Stage</Button>
                  </div>
                  {pipeline.stages?.sort((a, b) => a.position - b.position).map((stage) => (
                    <div key={stage.id} className="flex items-center justify-between p-3 border rounded-md">
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                        {stage.color && <div className="h-3 w-3 rounded-full" style={{ backgroundColor: stage.color }} />}
                        <span className="font-medium text-sm">{stage.name}</span>
                        <Badge variant="outline" className="text-xs">{stage.probability}%</Badge>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEditStage(pipeline.id, stage)}><Edit className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteStageInfo({ pipelineId: pipeline.id, stageId: stage.id })}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FormDialog open={showCreate} onOpenChange={setShowCreate} title="New Pipeline" description="Create a new sales pipeline" onSubmit={pipelineForm.handleSubmit(handleCreate)} isSubmitting={createPipeline.isPending}>
        <FormField form={pipelineForm} name="name" label="Pipeline Name" required />
        <FormTextarea form={pipelineForm} name="description" label="Description" />
      </FormDialog>

      <FormDialog open={!!editPipeline} onOpenChange={() => setEditPipeline(null)} title="Edit Pipeline" description="Update pipeline details" onSubmit={pipelineForm.handleSubmit(handleUpdatePipeline)} isSubmitting={updatePipeline.isPending}>
        <FormField form={pipelineForm} name="name" label="Pipeline Name" required />
        <FormTextarea form={pipelineForm} name="description" label="Description" />
      </FormDialog>

      <FormDialog open={!!addStageTo} onOpenChange={() => setAddStageTo(null)} title="Add Stage" description="Add a new stage to the pipeline" onSubmit={stageForm.handleSubmit(handleCreateStage)} isSubmitting={addStage.isPending}>
        <FormField form={stageForm} name="name" label="Stage Name" required />
        <FormField form={stageForm} name="probability" label="Win Probability (%)" type="number" />
        <FormField form={stageForm} name="color" label="Color (hex)" placeholder="#6366f1" />
      </FormDialog>

      <FormDialog open={!!editStageInfo} onOpenChange={() => setEditStageInfo(null)} title="Edit Stage" description="Update stage details" onSubmit={stageForm.handleSubmit(handleUpdateStage)} isSubmitting={updateStage.isPending}>
        <FormField form={stageForm} name="name" label="Stage Name" required />
        <FormField form={stageForm} name="probability" label="Win Probability (%)" type="number" />
        <FormField form={stageForm} name="color" label="Color (hex)" placeholder="#6366f1" />
      </FormDialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)} title="Delete Pipeline" description="This will delete the pipeline and all its stages. Are you sure?" variant="destructive" confirmLabel="Delete" onConfirm={() => { if (deleteId) { deletePipeline.mutate(deleteId); setDeleteId(null); } }} />
      <ConfirmDialog open={!!deleteStageInfo} onOpenChange={() => setDeleteStageInfo(null)} title="Delete Stage" description="Deals in this stage will need to be moved. Are you sure?" variant="destructive" confirmLabel="Delete" onConfirm={() => { if (deleteStageInfo) { deleteStage.mutate(deleteStageInfo); setDeleteStageInfo(null); } }} />
    </PageContainer>
  );
}
